import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ColDef, SortModelItem } from 'ag-grid-community';

import getGridRowCount from 'api/getGridRowCount';
import getGridRows from 'api/getGridRows';
import AnalyticsContext from 'contexts/AnalyticsContext';
import BoardSlideOutContext from 'contexts/BoardSlideOutContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import GqlClientContext from 'contexts/GqlClientContext';
import GridContext from 'contexts/GridContext';
import search from 'hooks/grids/search';
import { toSortField } from 'hooks/grids/useDatasource';
import useDateScope from 'hooks/useDateScope';
import aguid from 'lib/aguid';
import captureException from 'services/captureException';

// Relative: a bare 'constants' resolves to Node's built-in module
import { GRID_PAGE_SIZE } from '../../constants';

type Page = {
  documents: ElasticDocument[];
  // The first page's total, which says when the last page has loaded
  count?: number;
  searchAfter?: string;
};

// A records table's rows, a page at a time: the first page for the current
// sort/filters/date scope, then the next page each time the table nears the
// end of what's loaded. Replaces AG's server-side row model (useDatasource's
// datasource object, or ServerSideDataSource with the grid's query overrides)
// for DataTable. Pages come from search2 (searchAfter), or with
// offsetQueries (a special metric's own records and count queries) by offset,
// counting once on the first page.
const useInfiniteRows = ({
  dataType,
  includeFields,
  filterInput,
  sortModel,
  getColDef,
  rowEnricher,
  offsetQueries,
}: {
  dataType: string;
  includeFields: string[] | undefined;
  filterInput: FilterInput[];
  sortModel: SortModelItem[];
  getColDef: (colId: string) => ColDef | undefined;
  rowEnricher?: (row: ElasticDocument) => ElasticDocument;
  offsetQueries?: { rows: string; count: string };
}) => {
  const { client } = useContext(GqlClientContext);
  const { restartGrid, setIsLoadingData } = useContext(GridContext);
  const dateScope = useDateScope({});
  const boardSlideOutContext = useContext(BoardSlideOutContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { getBonusProgram } = useContext(DatasetDefinitionsContext);
  const bonusProgram = useMemo(
    () => getBonusProgram(dataType),
    [getBonusProgram, dataType],
  );
  const [gridSessionId] = useState<string>(aguid());

  const [rows, setRows] = useState<ElasticDocument[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  // Request bookkeeping lives in refs: a response is only applied if it
  // answers the latest request (sorting or filtering again makes any page in
  // flight stale)
  const lastRequestIdRef = useRef<number>(0);
  const searchAfterRef = useRef<string | undefined>();
  const isFetchingRef = useRef<boolean>(false);
  const loadedCountRef = useRef<number>(0);
  const totalCountRef = useRef<number | undefined>();

  const getColDefRef = useRef(getColDef);
  getColDefRef.current = getColDef;
  const sortBy = useMemo(
    () =>
      toSortField({
        sortModelItems: sortModel,
        getColDef: (colId) => getColDefRef.current(colId),
        bonusProgram,
      }),
    [bonusProgram, sortModel],
  );

  const enrich = useCallback(
    (documents: ElasticDocument[]) =>
      rowEnricher ? documents.map(rowEnricher) : documents,
    [rowEnricher],
  );

  const rowsQuery = offsetQueries?.rows;
  const countQuery = offsetQueries?.count;
  const fetchPage = useCallback(
    async ({ isFirstPage }: { isFirstPage: boolean }): Promise<Page> => {
      if (rowsQuery && countQuery) {
        const query = { client, dataType, filters: filterInput, dateScope };
        const [documents, count] = await Promise.all([
          getGridRows({
            ...query,
            query: rowsQuery,
            from: isFirstPage ? 0 : loadedCountRef.current,
            limit: GRID_PAGE_SIZE,
            sortBy,
          }),
          isFirstPage
            ? getGridRowCount({ ...query, query: countQuery })
            : Promise.resolve(undefined),
        ]);
        return { documents, count };
      }
      return search({
        dataType,
        sortBy,
        filters: filterInput,
        client,
        dateScope,
        includeFields,
        searchAfter: isFirstPage ? undefined : searchAfterRef.current,
      });
    },
    [
      client,
      countQuery,
      dataType,
      dateScope,
      filterInput,
      includeFields,
      rowsQuery,
      sortBy,
    ],
  );

  // First page
  useEffect(() => {
    const requestId = lastRequestIdRef.current + 1;
    lastRequestIdRef.current = requestId;
    searchAfterRef.current = undefined;
    isFetchingRef.current = true;
    setIsLoadingFirstPage(true);
    setIsLoadingData(true);

    fetchPage({ isFirstPage: true })
      .then((result) => {
        if (requestId !== lastRequestIdRef.current) {
          return;
        }
        trackEvent('Grid - Initial Page loaded', { dataType, gridSessionId });
        searchAfterRef.current = result.searchAfter;
        loadedCountRef.current = result.documents.length;
        totalCountRef.current = result.count;
        isFetchingRef.current = false;
        setRows(enrich(result.documents));
        setTotalCount(result.count);
        setIsLoadingFirstPage(false);
        setIsLoadingData(false);
      })
      .catch((ex) => {
        if (requestId !== lastRequestIdRef.current) {
          return;
        }
        captureException(ex);
        isFetchingRef.current = false;
        setIsLoadingFirstPage(false);
        setIsLoadingData(false);
      });
  }, [
    dataType,
    enrich,
    fetchPage,
    gridSessionId,
    refreshKey,
    setIsLoadingData,
    trackEvent,
  ]);

  const fetchNextPage = useCallback(() => {
    const total = totalCountRef.current;
    if (
      isFetchingRef.current ||
      total === undefined ||
      loadedCountRef.current >= total
    ) {
      return;
    }

    const requestId = lastRequestIdRef.current + 1;
    lastRequestIdRef.current = requestId;
    isFetchingRef.current = true;
    setIsLoadingData(true);

    fetchPage({ isFirstPage: false })
      .then((result) => {
        if (requestId !== lastRequestIdRef.current) {
          return;
        }
        searchAfterRef.current = result.searchAfter;
        loadedCountRef.current += result.documents.length;
        isFetchingRef.current = false;
        setRows((current) => [...current, ...enrich(result.documents)]);
        trackEvent('Grid - Additional Page loaded', {
          dataType,
          gridSessionId,
        });
        setIsLoadingData(false);
      })
      .catch((ex) => {
        if (requestId !== lastRequestIdRef.current) {
          return;
        }
        captureException(ex);
        isFetchingRef.current = false;
        trackEvent('Grid - Failed to load additional page', {
          dataType,
          gridSessionId,
        });
        // Outside a GridProvider (a performance board) there's no grid to
        // restart: the rows loaded so far stay
        if (restartGrid) {
          restartGrid();
        }
      });
  }, [
    dataType,
    enrich,
    fetchPage,
    gridSessionId,
    restartGrid,
    setIsLoadingData,
    trackEvent,
  ]);

  // A board slide-out that edits a record asks the grid to reload
  useEffect(() => {
    if (boardSlideOutContext && boardSlideOutContext.gridRequiresRefresh) {
      setRefreshKey((key) => key + 1);
      boardSlideOutContext.setGridRequiresRefresh(false);
    }
  }, [boardSlideOutContext]);

  return {
    rows,
    totalCount,
    hasMore: totalCount !== undefined && rows.length < totalCount,
    isLoadingFirstPage,
    fetchNextPage,
  };
};

export default useInfiniteRows;
